import type { OklchColor } from './types'
import { DEFAULT_SOURCE_COLOR } from './types'
import { harmonizeHue } from './harmonization'

const SEMANTIC_HUES = {
  success: 135,
  error: 25,
  warning: 75,
  info: 225,
} as const

/**
 * Injects minimal set of hue variables for CSS-first palette system.
 * CSS defines lightness/chroma.
 */
export function injectPaletteHues({
  element = document.documentElement,
  sourceColor = DEFAULT_SOURCE_COLOR,
  minimal = true,
  isDark = false,
}: {
  sourceColor?: OklchColor
  minimal?: boolean
  element?: HTMLElement
  isDark?: boolean
}) {
  element.style.setProperty('--source-hue', String(sourceColor.h))

  // Clear harmonized surface hues when not in minimal mode
  if (!minimal) {
    element.style.removeProperty('--secondary-hue')
    element.style.removeProperty('--on-secondary-hue')
    element.style.removeProperty('--surface-1-hue')
    element.style.removeProperty('--surface-2-hue')
    element.style.removeProperty('--surface-3-hue')
    element.style.removeProperty('--on-surface-hue')
    element.style.removeProperty('--on-surface-secondary-hue')
    element.style.removeProperty('--outline-hue')
    element.style.removeProperty('--border-hue')
    element.style.removeProperty('--separator-hue')
  }

  const hues = harmonizeHues(sourceColor, minimal, isDark)
  for (const [token, value] of Object.entries(hues)) {
    element.style.setProperty(`--${token}-hue`, String(value))
  }
}

/**
 * Calculates harmonized hue values based on source color and theme mode.
 * Returns map of variable names to hue values for CSS injection.
 *
 * * For the **chromatic** palette, it harmonizes **only** semantic colors.
 * * For the **minimal** palette, it **also** harmonizes plain surfaces, on-surface, on-surface-secondary, outline and border.
 * * Derived tokens such as semantic surfaces are composed entirely in CSS.
 */
export function harmonizeHues(
  sourceColor: OklchColor,
  minimal: boolean,
  isDark: boolean,
): Record<string, number> {
  const hues: Record<string, number> = {}

  // oxlint-disable-next-line unicorn/no-immediate-mutation -- idiomatic immutable-update: clone then mutate; complex conditional structure precludes literal form
  hues.success = harmonizeHue(SEMANTIC_HUES.success, sourceColor.h)
  hues.error = harmonizeHue(SEMANTIC_HUES.error, sourceColor.h)
  hues.warning = harmonizeHue(SEMANTIC_HUES.warning, sourceColor.h)
  hues.info = harmonizeHue(SEMANTIC_HUES.info, sourceColor.h)

  if (minimal) {
    const baseHue = isDark ? SEMANTIC_HUES.info : SEMANTIC_HUES.error // warmer in light mode, cooler in dark mode.

    hues.secondary = harmonizeHue(baseHue, sourceColor.h)
    hues['on-secondary'] = harmonizeHue(baseHue, sourceColor.h)
    hues['surface-1'] = harmonizeHue(baseHue, sourceColor.h)
    hues['surface-2'] = harmonizeHue(baseHue, sourceColor.h)
    hues['surface-3'] = harmonizeHue(baseHue, sourceColor.h)
    hues['on-surface'] = harmonizeHue(baseHue, sourceColor.h)
    hues['on-surface-secondary'] = harmonizeHue(baseHue, sourceColor.h)
    hues.outline = harmonizeHue(baseHue, sourceColor.h)
    hues.border = harmonizeHue(baseHue, sourceColor.h)
    hues.separator = harmonizeHue(baseHue, sourceColor.h)
  }

  return hues
}
